import { Close } from "@mui/icons-material"
import { Divider, Drawer, IconButton, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import ObjectDisplayTable from "../ObjectDisplayTable"

/**
 * The whole `locker` Thing as a searchable key/value table (LOCKER-355 §3.2).
 *
 * ## Why the Unit section needed its own viewer
 *
 * The page already had a raw-snapshot drawer, but it shows only
 * `locker.sstLocker` / `locker.sst2Locker` — the PARTNER's view. Every field
 * that decides how this section behaves is on the Mobilus side of the object:
 * `sstApiVersion`, `moduleMapping`, `sstLockerId`, `isVirtual`, the migration
 * markers. Those were reachable from no surface at all, which is why "which
 * integration am I even looking at" had no answer without opening the code.
 *
 * ## A sibling component, not more of `SaLockerPage`
 *
 * `SaLockerPage.tsx` was already over the ~400-line cap when this was written
 * (432), so this could not be inlined there — and it belongs beside the section
 * that opens it in any case.
 *
 * Read-only, deliberately and permanently: this is the diagnostic surface an
 * admin opens when they do not yet trust what the page is telling them, and an
 * editable field on it would invite changing the thing being diagnosed.
 * `ObjectDisplayTable` is reused rather than reimplemented — it already
 * flattens to dotted paths and filters on key path and value, which is exactly
 * the contract asked for.
 */
export const SaLockerObjectViewer: FC<{
  open: boolean
  onClose: () => void
  locker: Record<string, unknown>
}> = ({ open, onClose, locker }) => {
  const { t } = useTranslation()
  return (
    <Drawer data-ui="UI-SL2W-149"
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", md: "70vw" },
          maxWidth: 1280,
          pt: "64px",
        },
      }}
    >
      <Stack direction="column" spacing={2} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {t("saLockerConsole.lockerObject")}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label={t("close")}>
            <Close />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t("saLockerConsole.lockerObjectHint")}
        </Typography>
        <Divider />
        <ObjectDisplayTable data={locker} />
      </Stack>
    </Drawer>
  )
}

export default SaLockerObjectViewer
